import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api';
import { FloatingPanel } from '@/components/ui/floating-panel';
import { EditLockBanner } from '@/components/ui/edit-lock-banner';
import { useEditLock } from '@/hooks/useEditLock';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/input';
import { aFormulario, buildModelPatch, debeReiniciarFormulario, type FormModelo } from './model-patch';

export interface EditableModel {
  id: string;
  code: string;
  name: string;
  familyId: string | null;
  description: string | null;
  shape: string;
  widthMm: number;
  heightMm: number;
  thicknessMm: number;
  ledType: string | null;
  kelvin: number | null;
  powerW: number | null;
  isCustom: boolean;
  active: boolean;
  notes: string | null;
}

const FORMAS = [
  { value: 'rectangular', label: 'Rectangular' },
  { value: 'square', label: 'Cuadrada' },
  { value: 'round', label: 'Redonda' },
  { value: 'oval', label: 'Ovalada' },
  { value: 'custom', label: 'A medida' }
];

/** Ficha del modelo editable desde el propio modelo, sin volver al listado. */
export function ModelEditPanel({ model, open, onClose, onSaved }: {
  model: EditableModel | null; open: boolean; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<FormModelo | null>(null);
  const [saving, setSaving] = useState(false);
  // El modelo se bloquea al abrirlo para editar: dos personas cambiando medidas a la vez dejan una
  // BOM calculada con datos que ya no existen.
  const bloqueo = useEditLock('mirror_model', open && model ? model.id : null);
  const soloLectura = !bloqueo.canEdit;
  const familias = useQuery({
    queryKey: ['families'],
    queryFn: () => api<{ data: { id: string; code: string; name: string }[] }>('/families'),
    enabled: open
  });

  const abiertoPrevio = useRef(false);
  const idIniciado = useRef<string | null>(null);

  useEffect(() => {
    const reiniciar = debeReiniciarFormulario({
      open,
      abiertoPrevio: abiertoPrevio.current,
      modelIdActual: model?.id ?? null,
      modelIdIniciado: idIniciado.current
    });
    if (reiniciar) {
      setForm(model ? aFormulario(model) : null);
      idIniciado.current = model?.id ?? null;
    }
    // Al cerrar se vacía el formulario: si no, al reabrir se ven durante un frame los valores de la
    // sesión anterior antes de que el efecto los recargue.
    if (!open) { idIniciado.current = null; setForm(null); }
    abiertoPrevio.current = open;
  }, [open, model]);

  if (!model || !form) return null;
  const set = (campo: keyof FormModelo, valor: string | boolean) =>
    setForm((previo) => (previo ? { ...previo, [campo]: valor } : previo));

  const guardar = async () => {
    const cuerpo = buildModelPatch(form);
    if (cuerpo.name.length < 2) { toast.error('El nombre del modelo es obligatorio'); return; }
    if (cuerpo.widthMm <= 0 || cuerpo.heightMm <= 0) { toast.error('Ancho y alto deben ser mayores que cero'); return; }
    if (cuerpo.widthMm > 6000 || cuerpo.heightMm > 6000) { toast.error('Las medidas no pueden pasar de 6000 mm'); return; }
    if (cuerpo.thicknessMm < 1 || cuerpo.thicknessMm > 50) { toast.error('El grosor va entre 1 y 50 mm'); return; }
    if (cuerpo.kelvin != null && (cuerpo.kelvin < 1000 || cuerpo.kelvin > 10000)) { toast.error('La temperatura va entre 1000 y 10000 K'); return; }
    if (cuerpo.powerW != null && (cuerpo.powerW < 0 || cuerpo.powerW > 1000)) { toast.error('La potencia va entre 0 y 1000 W'); return; }
    setSaving(true);
    try {
      await api(`/mirror-models/${model.id}`, { method: 'PATCH', body: cuerpo });
      toast.success('Modelo actualizado');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FloatingPanel
      open={open} onClose={onClose}
      title={`Editar ${model.code}`}
      description="Medidas y potencia cambian lo que pide la BOM al calcular; la lista de componentes no se toca."
      width={520}
      footer={<><Button variant="outline" onClick={onClose}>Volver</Button>
        <Button loading={saving} disabled={soloLectura} onClick={() => void guardar()}>Guardar</Button></>}
    >
      <div className="space-y-3">
        {/* Fuera del fieldset: sus botones tienen que seguir activos en solo lectura. */}
        <EditLockBanner
          state={bloqueo.state}
          onTakeOver={() => void bloqueo.takeOver()}
          onRetry={() => void bloqueo.retry()}
        />
        <fieldset disabled={soloLectura} className="m-0 min-w-0 space-y-3 border-0 p-0">
        <Field label="Nombre" required>
          <Input aria-label="Nombre" value={form.name} maxLength={160} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Familia">
            <Select aria-label="Familia" value={form.familyId} onChange={(e) => set('familyId', e.target.value)}>
              <option value="">Sin familia</option>
              {(familias.data?.data ?? []).map((familia) => (
                <option key={familia.id} value={familia.id}>{familia.code} · {familia.name}</option>
              ))}
              {/* Sin permiso de artículos la lista llega vacía: la familia actual se muestra igual,
                  para que el desplegable no diga «Sin familia» y guarde otra cosa. */}
              {form.familyId && !(familias.data?.data ?? []).some((f) => f.id === form.familyId) ? (
                <option value={form.familyId}>Familia actual</option>
              ) : null}
            </Select>
          </Field>
          <Field label="Forma">
            <Select aria-label="Forma" value={form.shape} onChange={(e) => set('shape', e.target.value)}>
              {FORMAS.map((forma) => <option key={forma.value} value={forma.value}>{forma.label}</option>)}
            </Select>
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Ancho (mm)">
            <Input aria-label="Ancho" type="number" min="0" value={form.widthMm} onChange={(e) => set('widthMm', e.target.value)} />
          </Field>
          <Field label="Alto (mm)">
            <Input aria-label="Alto" type="number" min="0" value={form.heightMm} onChange={(e) => set('heightMm', e.target.value)} />
          </Field>
          <Field label="Grosor (mm)">
            <Input aria-label="Grosor" type="number" min="1" value={form.thicknessMm} onChange={(e) => set('thicknessMm', e.target.value)} />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Tipo de LED">
            <Input aria-label="Tipo de LED" value={form.ledType} maxLength={80} onChange={(e) => set('ledType', e.target.value)} />
          </Field>
          <Field label="Temperatura (K)">
            <Input aria-label="Temperatura" type="number" min="1000" max="10000" value={form.kelvin}
              onChange={(e) => set('kelvin', e.target.value)} />
          </Field>
          <Field label="Potencia (W)">
            <Input aria-label="Potencia" type="number" step="0.01" min="0" value={form.powerW}
              onChange={(e) => set('powerW', e.target.value)} />
          </Field>
        </div>
        <Field label="Descripción">
          <Input aria-label="Descripción" value={form.description} maxLength={1000} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <Field label="Notas" hint="Solo para el equipo; no sale en los documentos.">
          <Input aria-label="Notas" value={form.notes} maxLength={2000} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        <div className="flex flex-wrap gap-4 text-xs">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.isCustom} onChange={(e) => set('isCustom', e.target.checked)} />
            Hecho a medida
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
            Activo (se ofrece al crear pedidos)
          </label>
        </div>
        </fieldset>
      </div>
    </FloatingPanel>
  );
}
